import * as React from 'react'

import { cn } from './cn'

type TabValue = string | number

export interface TabsProps {
  value: TabValue
  onChange?: (event: React.SyntheticEvent, value: TabValue) => void
  variant?: 'standard' | 'fullWidth' | 'scrollable'
  centered?: boolean
  className?: string
  children?: React.ReactNode
  'aria-label'?: string
  indicatorColor?: string
  textColor?: string
  scrollButtons?: boolean | 'auto' | string
  allowScrollButtonsMobile?: boolean
}

const TabsContext = React.createContext<{
  value: TabValue
  onChange?: TabsProps['onChange']
  fullWidth?: boolean
}>({ value: 0 })

/**
 * Old MUI Tabs/Tab pair. Children <Tab/> elements get an implicit index
 * value when no explicit `value` prop is given, matching MUI behavior.
 */
const Tabs = ({
  value,
  onChange,
  variant = 'standard',
  centered,
  className,
  children,
  scrollButtons,
  allowScrollButtonsMobile,
  ...props
}: TabsProps) => {
  let index = -1
  const items = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child
    index += 1
    return React.cloneElement(
      child as React.ReactElement<TabProps>,
      {
        __value: (child.props as TabProps).value ?? index,
      } as Partial<TabProps>,
    )
  })

  return (
    <TabsContext.Provider
      value={{ value, onChange, fullWidth: variant === 'fullWidth' }}
    >
      <div
        role="tablist"
        aria-label={props['aria-label']}
        className={cn(
          'flex min-h-[44px] items-stretch gap-1 overflow-x-auto border-b border-divider px-1',
          centered && 'justify-center',
          className,
        )}
      >
        {items}
      </div>
    </TabsContext.Provider>
  )
}

export default Tabs

export interface TabProps {
  label?: React.ReactNode
  value?: TabValue
  icon?: React.ReactNode
  iconPosition?: 'top' | 'start' | 'end' | 'bottom'
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  onClick?: React.MouseEventHandler
  role?: string
  'aria-label'?: string
  /** Internal: resolved value injected by Tabs. */
  __value?: TabValue
}

export const Tab = ({
  label,
  icon,
  iconPosition = 'top',
  disabled,
  className,
  style,
  onClick,
  __value,
  ...aria
}: TabProps) => {
  const { value, onChange, fullWidth } = React.useContext(TabsContext)
  const selected = value === __value

  return (
    <button
      type="button"
      role="tab"
      aria-label={aria['aria-label']}
      aria-selected={selected}
      disabled={disabled}
      onClick={(e) => {
        onClick?.(e)
        onChange?.(e, __value as TabValue)
      }}
      style={style}
      className={cn(
        'relative inline-flex min-w-[72px] cursor-pointer items-center justify-center gap-1.5 rounded-t-lg px-3.5 py-2.5',
        'text-sm font-medium transition-colors',
        'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
        iconPosition === 'top' && 'flex-col',
        iconPosition === 'bottom' && 'flex-col-reverse',
        iconPosition === 'end' && 'flex-row-reverse',
        selected
          ? 'text-primary after:absolute after:inset-x-2 after:bottom-0 after:h-[2.5px] after:rounded-full after:bg-primary'
          : 'text-text-secondary hover:text-text-primary',
        fullWidth && 'grow basis-0',
        disabled && 'pointer-events-none opacity-40',
        className,
      )}
    >
      {icon}
      {label}
    </button>
  )
}
